import React from 'react';
import { useIntl } from 'react-intl';

import {
    CSSEditorComponentLabel,
    CSSEditorComponentWrapper,
    CSSEditorWidgetProperties,
} from 'components/Editors/components/Editor.style';
import { EditorColorPicker } from 'components/Editors/components/EditorColorPicker';

import { EditorSelect } from 'components/Editors/components/EditorSelect';

import { EditorColorsOptions, EditorRadiusOptions } from '../const';

import { WidgetDatasourceTableContent, WidgetEditorActions } from 'jamespot-user-api';

import { EditorCheckbox } from 'components/Editors/components/EditorCheckbox';

import { ensureIsNumber } from './ensure';
import { Typography } from 'components/JRCTypography/JRCTypography.styles';

const DEFAULT_COLUMNS_WIDTH = 200;
const DEFAULT_COLUMNS_OPTIONS = [200, 300, 400, 500];
const DEFAULT_LIMIT = 40;

export const JRCWidgetTableSharedEditor = ({
    limit = 20,
    tableHeadColor,
    tableHeadTextColor,
    tableHeadIconColor,
    tableRowColor,
    tableRowTextColor,
    tableBorderRadius,
    tableSizedColumns,
    tableSizedColumnsWidth,
    onChange,
}: WidgetDatasourceTableContent & Pick<WidgetEditorActions<WidgetDatasourceTableContent>, 'onChange'>) => {
    const intl = useIntl();

    return (
        <CSSEditorWidgetProperties>
            <CSSEditorComponentWrapper>
                <CSSEditorComponentLabel>
                    {intl.formatMessage({ id: 'WIDGET_Table_Sized_Columns' })}
                </CSSEditorComponentLabel>
                <Typography size={'s'}>
                    {intl.formatMessage({ id: 'WIDGET_Table_Sized_Columns_No_Mobile_Support' })}
                </Typography>
                <EditorCheckbox
                    initialValue={tableSizedColumns ?? false}
                    onChange={(tableSizedColumns) =>
                        onChange({ tableSizedColumns, tableSizedColumnsWidth: DEFAULT_COLUMNS_WIDTH })
                    }
                />
            </CSSEditorComponentWrapper>
            {tableSizedColumns ? (
                <CSSEditorComponentWrapper>
                    <EditorSelect
                        label={intl.formatMessage({ id: 'WIDGET_Table_Sized_Columns_Width' })}
                        options={DEFAULT_COLUMNS_OPTIONS.map((value) => {
                            return { label: String(value) + 'px', value: String(value) };
                        })}
                        required={true}
                        initialValue={String(tableSizedColumnsWidth)}
                        onChange={(tableSizedColumnsWidth) => {
                            if (
                                undefined !== tableSizedColumnsWidth &&
                                ensureIsNumber(Number(tableSizedColumnsWidth))
                            ) {
                                onChange({ tableSizedColumnsWidth: Number(tableSizedColumnsWidth) });
                            }
                        }}
                    />
                </CSSEditorComponentWrapper>
            ) : (
                <></>
            )}
            <CSSEditorComponentWrapper>
                <CSSEditorComponentLabel>
                    {intl.formatMessage({ id: 'WIDGET_Table_Head_Background_Color' })}
                </CSSEditorComponentLabel>
                <EditorColorPicker
                    colors={EditorColorsOptions}
                    initialValue={tableHeadColor}
                    onChange={(tableHeadColor) => onChange({ tableHeadColor })}
                />
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <CSSEditorComponentLabel>
                    {intl.formatMessage({ id: 'WIDGET_Table_Head_Text_Color' })}
                </CSSEditorComponentLabel>
                <EditorColorPicker
                    colors={EditorColorsOptions}
                    initialValue={tableHeadTextColor}
                    onChange={(tableHeadTextColor) => onChange({ tableHeadTextColor })}
                />
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <CSSEditorComponentLabel>
                    {intl.formatMessage({ id: 'WIDGET_Table_Head_Icon_Color' })}
                </CSSEditorComponentLabel>
                <EditorColorPicker
                    colors={EditorColorsOptions}
                    initialValue={tableHeadIconColor}
                    onChange={(tableHeadIconColor) => onChange({ tableHeadIconColor })}
                />
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <CSSEditorComponentLabel>
                    {intl.formatMessage({ id: 'WIDGET_Table_Row_Background_Color' })}
                </CSSEditorComponentLabel>
                <EditorColorPicker
                    colors={EditorColorsOptions}
                    initialValue={tableRowColor}
                    onChange={(tableRowColor) => onChange({ tableRowColor })}
                />
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <CSSEditorComponentLabel>
                    {intl.formatMessage({ id: 'WIDGET_Table_Row_Text_Color' })}
                </CSSEditorComponentLabel>
                <EditorColorPicker
                    colors={EditorColorsOptions}
                    initialValue={tableRowTextColor}
                    onChange={(tableRowTextColor) => onChange({ tableRowTextColor })}
                />
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <EditorSelect
                    label={intl.formatMessage({ id: 'WIDGET_Datasource_Limit' })}
                    options={[...Array(Math.round(DEFAULT_LIMIT))].map((_, index) => {
                        return { label: String(index + 1), value: String(index + 1) };
                    })}
                    required={true}
                    initialValue={String(limit)}
                    onChange={(limit) => {
                        if (undefined !== limit && ensureIsNumber(Number(limit))) {
                            onChange({ limit: Number(limit) });
                        }
                    }}
                />
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <EditorSelect
                    label={intl.formatMessage({ id: 'CSS_Property_Border_Radius' })}
                    options={EditorRadiusOptions}
                    required={true}
                    initialValue={String(tableBorderRadius)}
                    onChange={(tableBorderRadius) => {
                        if (undefined !== tableBorderRadius && ensureIsNumber(Number(tableBorderRadius))) {
                            onChange({ tableBorderRadius: Number(tableBorderRadius) });
                        }
                    }}
                />
            </CSSEditorComponentWrapper>
        </CSSEditorWidgetProperties>
    );
};
